// apiSlice.ts
import { createApi } from '@reduxjs/toolkit/query/react'
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { baseQueryWithReauth } from '../../features/ChatSlice';
import type {
    OnboardingState,
    ConsentPayload,
    VerificationResult,
    TrustBrowserPayload,
    ClearStatus,
    EpicFhirOrg,
    ProviderSearchHit
} from '../../types/onboarding';
import type { Interpretation, PanelLabel } from './healthWalletConstants';

const baseUrl = '/api/healthwallet';

// Re-export types for convenience
export type OnboardingStatus = OnboardingState;

// Terminal dataRequestStatus values (a run is done). Used by the SSE status-snapshot reconcile: a run's
// status only moves forward, so for the SAME dataRequestId a terminal snapshot supersedes a stale
// non-terminal onboarding read (the mount-read↔subscribe race). See project_hw_sse_central_service_plan.
export const TERMINAL_DATA_REQUEST_STATUS = new Set<string>(['completed', 'failed']);

// PHASE 1 device-proof gate: the slice used to bridge `clearVerifiedInFlow`
// across the AuthBootstrap-forced reload by reading `?clear-result` from the
// URL. That URL hop is gone — the server now derives `clearVerifiedInFlow`
// from its JSONB `setAt` (set during CLEAR exchange success) and returns it
// directly on the /onboarding/status response. The slice machinery is left
// in place for now (cleared by onQueryStarted, overlaid by
// getCachedOnboardingState — both become no-ops since the value is always
// null) so downstream consumers don't need to change. See
// HW_CLEAR_PROOF_COOKIE_SPEC.md.
const _initialClearVerifiedInFlow: ClearStatus | null = null;

export const clearVerificationSlice = createSlice({
    name: 'clearVerification',
    initialState: { clearVerifiedInFlow: _initialClearVerifiedInFlow as ClearStatus | null },
    reducers: {
        setClearVerifiedInFlow: (state, action: PayloadAction<ClearStatus | null>) => {
            state.clearVerifiedInFlow = action.payload;
        },
    },
});

export const { setClearVerifiedInFlow } = clearVerificationSlice.actions;
export const clearVerificationReducer = clearVerificationSlice.reducer;

/**
 * Read the current cached OnboardingState from the RTK store, augmented with
 * clearVerifiedInFlow from the dedicated slice. Used by every state-touching
 * endpoint to ship the client's current state to the stateless server.
 */
const getCachedOnboardingState = (api: any): OnboardingState | null => {
    try {
        const selectStatus = healthWalletApi.endpoints.getOnboardingStatus.select();
        const result = selectStatus(api.getState());
        const cached = (result?.data as OnboardingState) ?? null;

        const clearVerifiedInFlow = (api.getState() as any).clearVerification?.clearVerifiedInFlow as ClearStatus | null;
        if (clearVerifiedInFlow) {
            return { ...(cached ?? {}), clearVerifiedInFlow } as OnboardingState;
        }
        return cached;
    } catch {
        return null;
    }
};

// Mutation response types - matching server responses exactly
export interface ConsentResult {
    success: boolean;
    signedAt: string;
    state: OnboardingState;
}

export interface VerificationOAuthResult {
    processed: boolean;
    clear_status: ClearStatus;
    verification_id: string;
    previous_verification_id: string | null;
    clear_user_id: string;
    state: OnboardingState;
}

export interface TrustBrowserResult {
    success: boolean;
    state: OnboardingState;
}

export interface DeleteHWDataResult {
    status: string;
}

export interface DataRequestResult {
    status: 'pending' | 'initiated';
    message: string;
    workflowResponse: any;
    state: OnboardingState;
}

// Types for grouped API responses
interface GroupedSummary {
    name: string;
    count: number;
    firstDate: string;
    lastDate: string;
    hasChart?: boolean;
    resource?: string;
}

interface GroupedResponse {
    named: GroupedSummary[];
    other: GroupedSummary | null;
    totalRecords: number;   // X — deduped record count (the hero volume)
    rawRecords: number;     // pre-dedup count of this tab's records; D (duplicates removed) = rawRecords − totalRecords
}

// (SummaryResponse / DashboardResponse / DashboardPatient / DashboardGroupedData / CompositionRecord /
//  ClinicalNoteRecord response types DELETED 2026-07-10 with the legacy /dashboard//summary endpoints —
//  records now use LandingResponse / NotesPageResponse / GroupedResponse / NoteItem below.)

interface ChartPoint {
    date: string;
    value: number;
    eventIndex: number;
}

export interface ChartData {
    unit: string;
    data: ChartPoint[];
}

export interface ChartSummaryStreamData {
    status: string | null;       // live step text while generating
    summary: string | null;      // the summary section (null = none / soft-fail → caller hides)
    questions: string[] | null;  // the doctor-questions section (type='questions')
    loading: boolean;            // true until the terminal `done`
}

// ===== MVP #3 per-slice records (the get_patient_summary split — served under /records/*) =====
export interface RecordsCounts {
    conditions: number;
    encounters: number;
    observations: number;
    procedures: number;
    allergies: number;
    medications: number;
    user_documents: number;
}
export interface NoteItem {
    id: string;
    kind: 'composition' | 'document';
    title: string | null;
    date: string | null;
    type: string | null;
    bucket?: string; // V1 note category (visit|lab|imaging|instructions|procedure|other)
    hasBinary: boolean;
    narrative: string | null; // inline for the returned page (sanitize before render); null when hasBinary
}
export interface NotesPageResponse {
    total: number;
    page: number;
    size: number;
    // Per-bucket counts (q-scoped, not type-scoped) — the V1 pills' badges.
    buckets?: Record<string, number>;
    notes: NoteItem[];
}
export interface LandingResponse {
    patient: { name: any[]; gender: string; birth_date: string; patient_id: string; identifiers: any[] };
    counts: RecordsCounts;
    notes: NotesPageResponse;
}
export interface NoteContentResponse {
    id: string;
    kind: 'composition' | 'document';
    contentType: string | null;
    narrative: string | null; // decoded text (sanitize before render)
    data: string | null;      // base64 payload when binary (build a data: URL to download)
}
export interface TrendGroup {
    name: string;
    // Analyte KEY (standard-code→name identity) — matches the Obs card's `key`; the FE uses it to look up the
    // trend series for an Obs card and to scope the AI chart-context (sent as the `name` field, which the backend
    // treats as the key). Distinct from the human `name` above.
    key?: string | null;
    resource: string;         // 'observations' | 'medications'
    unit: string | null;
    count: number;
    firstDate: string | null;
    lastDate: string | null;
    // Dominant reference range (labs; null for vitals/range-less measures → V1 "Data only" bucket).
    refLow?: number | null;
    refHigh?: number | null;
    // Text-form range ("<5.7", "Negative") — display-only, never drives bucketing.
    refText?: string | null;
    // Latest reading's OWN flag (Observation.interpretation) — overrides the sparkline color (green only when
    // in-range AND flagged normal) + shows as the card chip; consistent with the Observations tab.
    interpretation?: Interpretation | null;
    // Latest reading's panel interpretation (containing DiagnosticReport conclusion + name) — SECONDARY label.
    panel?: PanelLabel | null;
    data: { date: string | null; value: number }[];
}
// One COLLAPSED visit from get_patient_encounters (V1 Encounters tab): copies of the same visit
// across sources fold into one card; `ids` = every copy's fhir_id (the key for related items).
export interface EncounterVisit {
    day: string;               // YYYY-MM-DD
    start?: string | null;
    end?: string | null;
    type?: string | null;      // source type display ("HEMATOLOGY PHYSICIAN", "Outpatient", …)
    classCode?: string | null; // FHIR class (AMB/IMP/EMER/VR) or Epic's proprietary integers
    status?: string | null;
    org?: string | null;
    provider?: string | null;
    records: number;
    ids: string[];
    linked?: { conditions?: number; medications?: number; labs?: number; procedures?: number; reports?: number };
    isUpcoming?: boolean;      // present+true ONLY for scheduled/future appointments (backend moodCode classification)
}
export interface EncounterRelated {
    conditions?: string[];
    medications?: string[];
    labs?: string[];
    procedures?: string[];
    reports?: string[];
}
export interface ResourceEvent {
    name: string | null;
    start: string | null;
    value: number | null;
    unit: string | null;
    details: any;
}

// ===== USER-UPLOADED DOCUMENTS =====
// One user-uploaded document + its extraction status. Source-agnostic shape so the list
// component can later surface kno2/Epic docs too (USER_DOCUMENTS_UI_PLAN.md §7).
export interface UserDocument {
    id: string;
    org_id: string;
    document_name: string;
    mime_type: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | string;
    processing_error: string | null;
    received_at: string;
    processed_at: string | null;
}

export interface UserDocumentContent {
    mime_type: string;
    encoding: string;
    content_base64: string;
}

//  baseQuery: baseQueryWithReauth,
export const healthWalletApi = createApi({
    reducerPath: 'healthWalletApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Patient', 'Onboarding', 'SSE'],
    endpoints: (builder) => ({
        // getDashboard / getPatientInfo / getSummary / getGroupedResource / getGroupEvents DELETED 2026-07-10 —
        // legacy /dashboard, /summary, /${resource} endpoints removed; records use the /records/* queries below.
        // AI chart summary / doctor-questions over SSE. The map (/chart-summary-stream) relays HW-AI's
        // section-model events (status/summary/questions/done). We seed the cache entry, then stream updates
        // into it via onCacheEntryAdded — so the result lives in the RTK cache keyed per-arg
        // {resource,name,type,profileId}: a measure can NEVER inherit another's entry (the hand-rolled module
        // Map that used to back this caused exactly that wrong-summary bug). `type` routes summary vs questions.
        // ✅ Used in: ChartAiSummary.tsx (ValueChart / Trends detail / doctor-questions accordion).
        getChartSummaryStream: builder.query<ChartSummaryStreamData, { resource: string; name: string; type: 'summary' | 'questions'; profileId: string }>({
            // Seed only — the real data streams in below. No baseQuery call; the stream carries the `id` auth header.
            queryFn: (arg) => ({
                data: {
                    status: arg.type === 'questions' ? 'Drafting questions for your doctor…' : 'Analyzing your trend…',
                    summary: null,
                    questions: null,
                    loading: true,
                },
            }),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                const controller = new AbortController();
                // Abort the stream the moment this cache entry is dropped (last subscriber unmounted).
                cacheEntryRemoved.then(() => controller.abort()).catch(() => {});
                try {
                    await cacheDataLoaded; // seed is in cache
                    const response = await fetch(`${baseUrl}/chart-summary-stream`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', id: arg.profileId },
                        body: JSON.stringify({ resource: arg.resource, name: arg.name, type: arg.type }),
                        signal: controller.signal,
                    });
                    const reader = response.body?.getReader();
                    if (!reader) throw new Error('No response stream');
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let settled = false;
                    while (!settled) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || ''; // keep the incomplete trailing line
                        let eventType = '';
                        for (const line of lines) {
                            if (line.startsWith('event: ')) {
                                eventType = line.slice(7).trim();
                            } else if (line.startsWith('data: ')) {
                                try {
                                    const data = JSON.parse(line.slice(6));
                                    if (eventType === 'status') {
                                        updateCachedData((d) => { d.status = data.message ?? d.status; });
                                    } else if (eventType === 'summary') {
                                        updateCachedData((d) => { d.summary = data.summary ?? null; });
                                    } else if (eventType === 'questions') {
                                        updateCachedData((d) => { d.questions = Array.isArray(data.items) ? data.items : []; });
                                    } else if (eventType === 'done') {
                                        settled = true;
                                        updateCachedData((d) => { d.status = null; d.loading = false; });
                                    }
                                    // 'error' is log-only on the wire; a section that never arrives stays hidden.
                                } catch { /* keep-alive ping / partial frame — ignore */ }
                                eventType = '';
                            }
                        }
                    }
                    // Stream closed without a terminal 'done' (edge) → stop the spinner with whatever arrived.
                    updateCachedData((d) => { d.status = null; d.loading = false; });
                } catch {
                    // Aborted (unmount) or network/parse failure → stop the spinner; sections that arrived stay.
                    updateCachedData((d) => { d.status = null; d.loading = false; });
                }
            },
        }),

        // ===== MVP #3 PER-SLICE RECORDS (/records/*) — the get_patient_summary split =====
        // Strict pass-throughs to the per-slice PG functions. All tag ['Patient'] so the central SSE
        // refresh (data-refresh / workflow-completed) re-fetches them for free. project_hw_records_rendering_split.

        // Landing: patient header + per-category counts + notes page 1 (the default tab) in ONE call.
        getLanding: builder.query<LandingResponse, void>({
            query: () => `${baseUrl}/records/landing`,
            providesTags: ['Patient'],
        }),
        // Notes tab feed (the paged body BELOW the landing teaser). RTK infiniteQuery so each "Show more"/scroll
        // APPENDS the next page instead of re-fetching a growing page 1 — flat transfer, not cumulative. The FE
        // sends ONLY the page number; the page SIZE is the server's env knob (HW_NOTES_PAGE_SIZE, default 1) — the
        // FE just appends whatever comes back. The landing already renders page 1 (the teaser at the same env
        // size), so the feed starts at page 2. Kept LAZY in the component (skipped until the first pull) so it
        // doesn't fetch on mount and defeat the teaser. providesTags:['Patient'] → a sync invalidation refetches
        // the loaded pages (RTK owns accumulate + reset).
        notesFeed: builder.infiniteQuery<NotesPageResponse, void, number>({
            infiniteQueryOptions: {
                initialPageParam: 2, // page 1 is the landing teaser; the feed appends pages 2+
                getNextPageParam: (lastPage, _allPages, lastPageParam) =>
                    // page N covers notes [(N-1)*size, N*size); another page exists iff that upper bound < total.
                    lastPage.page * lastPage.size < lastPage.total ? lastPageParam + 1 : undefined,
            },
            query: ({ pageParam }) => `${baseUrl}/records/notes?page=${pageParam}`,
            providesTags: ['Patient'],
        }),
        // V1 Notes tab feed — SELF-CONTAINED (no landing teaser): starts at page 1 and carries the
        // server-side search + bucket filter; a q/type change is a NEW cache entry (pagination resets).
        notesFeedV1: builder.infiniteQuery<NotesPageResponse, { q?: string; type?: string }, number>({
            infiniteQueryOptions: {
                initialPageParam: 1,
                getNextPageParam: (lastPage, _allPages, lastPageParam) =>
                    lastPage.page * lastPage.size < lastPage.total ? lastPageParam + 1 : undefined,
            },
            query: ({ queryArg, pageParam }) => {
                const p = new URLSearchParams({ page: String(pageParam) });
                if (queryArg?.q) p.set('q', queryArg.q);
                if (queryArg?.type) p.set('type', queryArg.type);
                return `${baseUrl}/records/notes?${p.toString()}`;
            },
            providesTags: ['Patient'],
        }),
        // One note's full body/base64 — lazy on expand/download (page notes already carry narrative inline).
        getNoteContent: builder.query<NoteContentResponse, { id: string }>({
            query: ({ id }) => `${baseUrl}/records/note-content?id=${encodeURIComponent(id)}`,
            providesTags: ['Patient'],
        }),
        // Flip HW's instance-global read-time dedup toggle (Settings dev instrument; SET-only — resting
        // state is true on both sides, so the toggle initializes ON without a read). Invalidates
        // ['Patient'] so every records screen refetches in the new mode (else stale-mode caches linger).
        // See project_hw_resource_dedup_feature.
        setDedup: builder.mutation<{ dedup: boolean }, { dedup: boolean }>({
            query: (body) => ({ url: `${baseUrl}/records/dedup`, method: 'POST', body }),
            invalidatesTags: ['Patient'],
        }),
        // Trends: chartable groups WITH their series (lazy pill).
        getTrends: builder.query<TrendGroup[], void>({
            query: () => `${baseUrl}/records/trends`,
            providesTags: ['Patient'],
        }),
        // Structured tab tier-1: grouped list ({named, other, totalRecords, rawRecords}) — lazy on tab open.
        getResource: builder.query<GroupedResponse, string>({
            query: (resource) => `${baseUrl}/records/resource/${resource}`,
            providesTags: ['Patient'],
        }),
        // Structured tab tier-2: events under one group (name='' → the nameless "Other" bucket) — lazy on drill-down.
        getResourceEvents: builder.query<ResourceEvent[], { resource: string; name: string }>({
            query: ({ resource, name }) => `${baseUrl}/records/resource/${resource}/events?name=${encodeURIComponent(name)}`,
            providesTags: ['Patient'],
        }),
        // V1 Encounters tab: chronological visit list (collapsed across sources, linked counts) — lazy on tab open.
        getEncounters: builder.query<EncounterVisit[], void>({
            query: () => `${baseUrl}/records/encounters`,
            providesTags: ['Patient'],
        }),
        // One visit's named related items (detail dialog) — POST because `ids` is the visit's collapsed fhir_id array.
        getEncounterRelated: builder.query<EncounterRelated, { ids: string[] }>({
            query: ({ ids }) => ({ url: `${baseUrl}/records/encounter-related`, method: 'POST', body: { ids } }),
            providesTags: ['Patient'],
        }),

        // ===== ONBOARDING ENDPOINTS =====

        // Check if user has completed onboarding. POST with the current cached state
        // in the body under the `onboardingState` key — server is stateless and merges
        // its computed updates against what the client provides. Uniform field name
        // across all state-touching endpoints to avoid collisions (e.g. clear-process-oauth
        // uses `state` for the OAuth state token). Profile id continues to be injected
        // via headers by qryFuncWithSubHeader's prepareHeaders.
        getOnboardingStatus: builder.query<OnboardingStatus, void>({
            queryFn: async (_arg, api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/status`,
                    method: 'POST',
                    body: { onboardingState: getCachedOnboardingState(api) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as OnboardingStatus };
            },
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    // clearVerifiedInFlow has been sent to the server and echoed back
                    // into the RTK cache via the response — clear the bootstrap slice.
                    dispatch(setClearVerifiedInFlow(null));
                } catch {}
            },
            providesTags: ['Onboarding'],
        }),

        // Explicit dismissal of the post-CLEAR outcome screen. Clears the server-side
        // in-flow marker (browserTrust JSONB verificationId) so future /status calls
        // stop returning clearVerifiedInFlow. Fired from OnboardingFlow — success and
        // failure screens fire at different moments (see comment there).
        // Deliberately NO tag invalidation: a refetch here would return
        // clearVerifiedInFlow: null and yank the screen the user is looking at.
        acknowledgeClearVerification: builder.mutation<{ ok: boolean }, void>({
            queryFn: async (_arg, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/acknowledge-verification`,
                    method: 'POST',
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { ok: boolean } };
            },
        }),

        // Record user consent
        submitConsent: builder.mutation<ConsentResult, ConsentPayload>({
            queryFn: async (consentData, api, _extra, baseQuery) => { // ✅ Used in: OnboardingConsent.tsx
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/consent`,
                    method: 'POST',
                    body: { ...consentData, onboardingState: getCachedOnboardingState(api) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as ConsentResult };
            },
            // No cache invalidation - mutation returns updated state directly
        }),

        // Withdraw ONE optional research consent (Settings). Server deletes the key from the consent
        // map and returns the state with it stripped — mergeOnboardingState spreads consents, so the
        // response is what removes the stale `granted: true` from the client's cached base state.
        // Required consents are not revocable here; withdrawing those means deleting HW data.
        revokeResearchConsent: builder.mutation<{ success: boolean; consentKey: string; state: OnboardingState }, { consentKey: string }>({
            queryFn: async ({ consentKey }, api, _extra, baseQuery) => { // ✅ Used in: HealthWalletSettings.tsx
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/consent/revoke`,
                    method: 'POST',
                    body: { consentKey, onboardingState: getCachedOnboardingState(api) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { success: boolean; consentKey: string; state: OnboardingState } };
            },
            // No cache invalidation — the mutation returns the authoritative state; the caller patches
            // the onboarding-status cache with it (a refetch would race the just-written delete).
        }),

        // Start CLEAR identity verification
        startVerification: builder.mutation<VerificationResult, void>({
            query: () => ({ // ✅ Used in: OnboardingVerification.tsx, ReAuthFlow.tsx
                url: `${baseUrl}/onboarding/verify`,
                method: 'POST',
            }),
            // No cache invalidation - verification endpoint redirects, state updated via clear-process-oauth
        }),

        // Process CLEAR OAuth code. The mutation arg's `state` field is the OAuth
        // state token (used by the server-side OAuth flow), not the OnboardingState.
        // OnboardingState is sent separately under `onboardingState` (uniform body
        // key across all endpoints; see comment on getOnboardingStatus).
        processClearOAuth: builder.mutation<VerificationOAuthResult, { code: string; state: string; profileId?: number }>({
            queryFn: async (mutationArg, api, _extra, baseQuery) => { // ✅ Used in: AuthBootstrap.ts
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/clear-process-oauth`,
                    method: 'POST',
                    body: { ...mutationArg, onboardingState: getCachedOnboardingState(api) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as VerificationOAuthResult };
            },
            // No cache invalidation - mutation returns updated state directly
        }),

        // Delete all Health Wallet data for the current user
        deleteHWData: builder.mutation<DeleteHWDataResult, { id: number }>({
            query: ({ id }) => ({ // ✅ Used in: HealthWalletSettings.tsx
                url: '/api/DeleteUserHWData',
                method: 'DELETE',
                params: { id },
            }),
            invalidatesTags: ['Onboarding', 'Patient'],
        }),

        // Set browser trust preference
        setBrowserTrust: builder.mutation<TrustBrowserResult, TrustBrowserPayload>({
            queryFn: async (trustData, api, _extra, baseQuery) => { // ✅ Used in: OnboardingFlow.tsx, OnboardingTrustBrowser.tsx, ReAuthFlow.tsx
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/trust-browser`,
                    method: 'POST',
                    body: { ...trustData, onboardingState: getCachedOnboardingState(api) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as TrustBrowserResult };
            },
            // No cache invalidation - mutation returns updated state directly
        }),

        // Trigger backend data request from QHIN networks
        // requestHealthData: builder.mutation<DataRequestResult, { triggerWorkflow?: boolean }>({
        //     query: ({ triggerWorkflow = false }) => ({ // ✅ Used in: (not currently imported)
        //         url: `${baseUrl}/onboarding/request-data`,
        //         method: 'POST',
        //         body: { triggerWorkflow },
        //     }),
        //     // Keep Patient tag invalidation since data request affects patient data
        requestHealthData: builder.mutation<{ requestId: string; status: string; message: string; state: OnboardingState;[key: string]: any }, { type?: string; iasRequestId?: string; orgIndex?: number; forceReauth?: boolean; forceReprocessCcda?: boolean } | void>({
            queryFn: async (arg, api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/request-data`,
                    method: 'POST',
                    body: { onboardingState: getCachedOnboardingState(api), ...(arg ?? {}) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { requestId: string; status: string; message: string; state: OnboardingState;[key: string]: any } };
            },
            // have to wait for completion since this call only triggers retrieval that actually happens in setImmediate =, so no Patient tag invalidation here
            //invalidatesTags: ['Patient'],
        }),

        // Onboarding: run patient discovery SYNCHRONOUSLY → creates the discovery ias_request + returns the
        // discovered orgs. Invalidates 'Onboarding' so /onboarding/status refetches and surfaces epicFhirOrgs
        // (with per-org status) + kno2Orgs for the "Find my providers" / Connect step. The response also carries
        // epicOrgs + request_id for an immediate render before the refetch lands. See project_hw_unified_data_retrieval.
        patientDiscovery: builder.mutation<
            { request_id: string; patient_id: string; totalMatches: number; epicOrgs: any[];[key: string]: any },
            { organizationIds?: string[]; governance?: string; queryType?: string; documentRetrieveOptions?: any } | void
        >({
            queryFn: async (arg, api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/patient-discovery`,
                    method: 'POST',
                    body: { onboardingState: getCachedOnboardingState(api), ...(arg ?? {}) },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { request_id: string; patient_id: string; totalMatches: number; epicOrgs: any[];[key: string]: any } };
            },
            invalidatesTags: ['Onboarding'],
        }),

        // Onboarding: mark a discovered Epic org as SKIPPED (soft gate — user declines to connect it now).
        // Does NOT invalidate 'Onboarding' — the caller optimistically flips the org's status to 'skipped' in
        // the getOnboardingStatus cache so the guided connect loop advances INSTANTLY (no refetch). The server
        // persists the flag → a later genuine refetch/reload agrees. See OnboardingProviders.handleSkip.
        skipOrg: builder.mutation<
            { success: boolean; request_id: string; orgIndex: number; skipped: boolean },
            // `skipped` optional (default true = skip); false = un-skip ("Start over").
            { iasRequestId: string; orgIndex: number; skipped?: boolean }
        >({
            queryFn: async (arg, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/skip-org`,
                    method: 'POST',
                    body: { ...arg },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { success: boolean; request_id: string; orgIndex: number; skipped: boolean } };
            },
            // NO invalidatesTags — caller optimistically updates the cache (instant loop advance); server persists.
        }),

        // Search Epic's published provider directory for a provider Kno2 discovery did not match.
        // Read-only and PHI-free, but gated like its siblings; see the relay's note. A QUERY (not a
        // mutation) so RTK caches per search term — the picker re-queries as the patient types.
        providerSearch: builder.query<{ organizations: ProviderSearchHit[]; count: number }, string>({
            queryFn: async (q, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/provider-search?q=${encodeURIComponent(q)}`,
                    method: 'GET',
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { organizations: ProviderSearchHit[]; count: number } };
            },
            // No tags. The directory itself is reference data refreshed weekly server-side — but note
            // each hit also carries `alreadyAdded`, which IS per-patient state and DOES change when
            // they add one. Safe because the picker clears the search box on a successful add (so the
            // stale list stops being rendered), and useLazyQuery's trigger refetches by default rather
            // than serving the cache — a re-search of the same term re-asks the server. If a caller
            // ever triggers with preferCacheValue, it can show an Add button for an org already added;
            // harmless (add-org re-checks and answers alreadyPresent) but it would look wrong.
        }),

        // Append a provider the patient named to their discovery orgs. The server returns the new row
        // in the EXACT epic_fhir_orgs shape so the caller can append it to the getOnboardingStatus
        // cache and get something byte-identical to a refetch — a hand-built client copy is how the
        // onboarding-bypass synthetic state drifted and broke the Settings buttons.
        // `alreadyPresent: true` ⇒ that FHIR endpoint was already in the list (the existing entry wins);
        // the caller should highlight the returned index rather than append.
        addOrg: builder.mutation<
            { success: boolean; alreadyPresent?: boolean; request_id: string; orgIndex: number; org: EpicFhirOrg },
            // No request id: the SERVER picks which ias_requests row holds the org list, using the
            // same query the status endpoint reads it with. A client cannot choose correctly —
            // dataRequestId is the active request, which a per-org refresh makes a row with no orgs.
            // facilityId (when the hit was a facility) carries that site's street address onto the org.
            { brandId: string; facilityId?: string | null }
        >({
            queryFn: async (arg, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/add-org`,
                    method: 'POST',
                    body: { ...arg },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { success: boolean; alreadyPresent?: boolean; request_id: string; orgIndex: number; org: EpicFhirOrg } };
            },
            // NO invalidatesTags — same reasoning as skipOrg: the caller patches the cache with the
            // row the server just returned, so the list updates instantly through an Epic-redirect
            // flow where a refetch round-trip would be felt.
            // `restored: true` ⇒ the patient had DELETED this provider and picked it again; the server
            // un-marked the existing entry in place (same index), so callers treat it as a fresh add.
        }),

        // Delete a provider the patient added by mistake. Server-guarded: only an addedByUser org, and
        // only while nothing has been retrieved from it — a 409 `records_retrieved` is the normal
        // "we already have their records" answer, NOT a failure, and the caller should show its message.
        removeOrg: builder.mutation<
            { success: boolean; request_id: string; orgIndex: number; removed: boolean; verificationRevoked?: boolean },
            { iasRequestId: string; orgIndex: number; removed?: boolean }
        >({
            queryFn: async (arg, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `${baseUrl}/onboarding/remove-org`,
                    method: 'POST',
                    body: { ...arg },
                });
                if (result.error) return { error: result.error };
                return { data: result.data as { success: boolean; request_id: string; orgIndex: number; removed: boolean; verificationRevoked?: boolean } };
            },
            // NO invalidatesTags — the caller drops the row from the cache optimistically and rolls back
            // on failure, so the list reacts instantly (same contract as skipOrg/addOrg).
        }),

        // ===== USER-UPLOADED DOCUMENTS =====
        // No dedicated tag: the section owns getUserDocuments and refreshes via refetch()
        // (the summary reuses the Patient tag). See USER_DOCUMENTS_UI_PLAN.md §0/§3.

        // List the patient's uploaded documents + extraction status. patient_id is resolved
        // server-side from the profile header. Tagged 'Patient' so it's a first-class part of the
        // HW data refresh: the central SSE handler's terminal branch (workflow-completed) and
        // data-refresh branch both invalidate 'Patient', so the list + pill flip processing→✓ and
        // delete-propagation work on EVERY client/map — identical to how the summary refreshes,
        // instead of relying on a component-local SSE effect (which was racy — see prior
        // "user-doc spinner SSE-miss" debt). The extra refetch on unrelated Patient invalidations
        // (e.g. an IAS sync) is a cheap POST and is harmless.
        getUserDocuments: builder.query<{ success: boolean; documents: UserDocument[] }, void>({
            query: () => ({ url: `${baseUrl}/user-data/documents`, method: 'POST' }),
            providesTags: ['Patient'],
        }),

        // Upload a document (base64 in JSON). replaceOrgId set → user-driven replace of that slot.
        uploadUserDocument: builder.mutation<
            { success: boolean; documentId: string; orgId: string },
            { mimeType: string; contentBase64: string; description?: string; replaceOrgId?: string }
        >({
            query: (body) => ({ url: `${baseUrl}/user-data/upload`, method: 'POST', body }),
        }),

        // Retry extraction for an existing document (no re-upload).
        reprocessUserDocument: builder.mutation<{ success: boolean; documentId: string }, { document_id: string }>({
            query: (body) => ({ url: `${baseUrl}/user-data/reprocess`, method: 'POST', body }),
        }),

        // Permanently delete one uploaded document + its derived resources. NO invalidatesTags —
        // consistent with the rest of the feature, the caller manually dispatches invalidateTags(['Patient'])
        // (summary re-retrieves) + refetches the list on success. 409 when a sync/extraction is in progress.
        deleteUserDocument: builder.mutation<{ success: boolean; documentId: string }, { document_id: string }>({
            query: (body) => ({ url: `${baseUrl}/user-data/delete-document`, method: 'POST', body }),
        }),

        // Rename a document's display name in place (pure metadata; no re-extraction). Caller
        // refetches the list on success so the new name shows (consistent with the rest of the feature).
        renameUserDocument: builder.mutation<
            { success: boolean; documentId: string; document_name: string },
            { document_id: string; description: string }
        >({
            query: (body) => ({ url: `${baseUrl}/user-data/rename`, method: 'POST', body }),
        }),

        // Fetch a single document's stored content for inline viewing (lazy, per-row on expand).
        getUserDocumentContent: builder.query<UserDocumentContent, { document_id: string }>({
            query: (body) => ({ url: `${baseUrl}/user-data/document-content`, method: 'POST', body }),
        }),

        // SSE streaming query for real-time events
        getSSEEvents: builder.query<any, number>({
            queryFn: () => { // BREAKPOINT HERE
                console.log('[SSE] queryFn called');
                return { data: { events: [] } };
            },
            providesTags: ['SSE'],
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch, getState }) {
                // Get profileId from loggedin profile query
                const state = getState() as any;
                const lastQueryKey = Object.keys(state.profileApi?.queries ?? {}).filter((key) => key.startsWith('getLoggedInUserProfile')).pop();
                const loggedinProfileQuery = lastQueryKey ? state.profileApi?.queries[lastQueryKey] : undefined;
                const loggedinProfile = loggedinProfileQuery?.data as any;
                const profileId = loggedinProfile?.id;

                const fullUrl = `${baseUrl}/subscribe-to-complete-workflow-progress-updates-sse?profileId=${profileId}`
                console.log('[SSE] Connecting to:', fullUrl);

                // ONE long-lived EventSource per cache key. RTK ref-counts subscribers, so this
                // entry (and the connection) lives while ≥1 consumer is mounted and is torn down
                // on the last unmount — that is the mount-scoping. The connection is NO LONGER
                // closed on terminal events; it stays open for the next workflow and for
                // cross-client syncs. See healthWalletSyncService + project_hw_sse_central_service_plan.
                const TERMINAL = new Set(['workflow-completed', 'workflow-failed', 'workflow-error']);
                let syncing = false; // true while a workflow is mid-flight on this connection

                const ws = new EventSource(fullUrl);
                ws.onopen = () => { console.log('[SSE] Connection opened'); };

                try {
                    await cacheDataLoaded;

                    ws.onmessage = (event) => {
                        let eventData: any;
                        try {
                            eventData = JSON.parse(event.data);
                        } catch (error) {
                            console.error('SSE parse error:', error, 'Raw data:', event.data);
                            return;
                        }
                        const type = eventData.type;

                        // Connect-time STATUS SNAPSHOT (getIasRequestInitialState) — NOT a live transition. It does
                        // NOT touch the progress log, flip `syncing`, or refetch anything. Stored on its own cache
                        // key; useSyncRunning merges it into dataRequestStatus BY RUN ID (same run → a terminal
                        // snapshot beats a stale non-terminal onboarding read, closing the mount-read↔subscribe
                        // race) with no extra status call. See project_hw_sse_central_service_plan.
                        if (type === 'status-snapshot') {
                            updateCachedData((draft) => {
                                draft.statusSnapshot = {
                                    dataRequestId: eventData.dataRequestId ?? null,
                                    dataRequestStatus: eventData.dataRequestStatus ?? null,
                                };
                            });
                            return;
                        }

                        eventData.timestamp = new Date().toISOString();
                        updateCachedData((draft) => {
                            if (!draft.events) draft.events = [];
                            draft.events.push(eventData);
                        });

                        if (type === 'data-refresh') {
                            // Generic "data changed" signal for non-workflow mutations (e.g. an
                            // HW-data delete from another tab/device). Just refetch authoritative
                            // state — no progress/syncing semantics, so don't flip `syncing` or
                            // reset the log. The refetch re-derives onboarding when data is gone.
                            dispatch(healthWalletApi.util.invalidateTags(['Patient', 'Onboarding']));
                        } else if (TERMINAL.has(type)) {
                            // LIVE workflow completion. The connect seed is now a 'status-snapshot' (handled above),
                            // so this branch only fires for a workflow that finished while we were LISTENING — a real
                            // transition with fresh data to pull. Refetch authoritative state (Patient → records;
                            // Onboarding → dataRequestStatus flips to completed/failed). Connection STAYS OPEN.
                            // See project_hw_sse_central_service_plan.
                            syncing = false;
                            dispatch(healthWalletApi.util.invalidateTags(['Patient', 'Onboarding']));
                        } else if (!syncing) {
                            // First event of a new workflow (idle → active):
                            //  (a) reset the log to THIS workflow's events only — the connection
                            //      is now long-lived, so without this `events[]` would accumulate
                            //      across every workflow for the whole session (unbounded). Keep the
                            //      event we just pushed as the first line.
                            //  (b) refetch onboarding status so dataRequestStatus shows 'processing'
                            //      even when optimistic state was lost to an OAuth redirect/reload.
                            syncing = true;
                            updateCachedData((draft) => { draft.events = eventData ? [eventData] : []; });
                            dispatch(healthWalletApi.util.invalidateTags(['Onboarding']));
                        }
                    };

                    ws.onerror = () => {
                        // Do NOT close — the browser's EventSource auto-reconnects (it reopens the
                        // same URL after its retry interval). The server keeps the connection open
                        // past terminal events, so an error here is a genuine network/proxy drop and
                        // native reconnect recovers it. Calling close() (the old behavior) would
                        // PREVENT reconnection.
                        console.warn('[SSE] connection error — relying on EventSource auto-reconnect. readyState:', ws.readyState);
                    };
                } catch {
                    // If cacheDataLoaded throws (entry removed before data loaded), close.
                    ws.close();
                }

                // Last subscriber unmounted (RTK removed the cache entry) → close the connection.
                await cacheEntryRemoved;
                ws.close();
            },
        }),
    }),
})

export const {
    useGetChartSummaryStreamQuery,   // ✅ Used in: ChartAiSummary.tsx (RTK streaming — summary + doctor questions)
    // MVP #3 per-slice records (/records/*)
    useGetLandingQuery,
    useNotesFeedInfiniteQuery,
    useNotesFeedV1InfiniteQuery,     // V1 Notes tab (self-contained feed w/ server-side search + bucket pills)
    useLazyGetNoteContentQuery,
    useSetDedupMutation,             // Settings dev toggle — read-time dedup (project_hw_resource_dedup_feature)
    useGetTrendsQuery,
    useLazyGetTrendsQuery,
    useGetResourceQuery,
    useLazyGetResourceQuery,
    useGetResourceEventsQuery,
    useLazyGetResourceEventsQuery,
    useGetEncountersQuery,           // V1 Encounters tab (chronological visit list)
    useGetEncounterRelatedQuery,     // V1 Encounters detail (related items)
    useRequestHealthDataMutation,    // ✅ Used in: (not currently imported)
    usePatientDiscoveryMutation,     // onboarding "Find my providers" (Slice 3)
    useSkipOrgMutation,              // onboarding Connect step — skip an Epic org (Slice 3)
    useLazyProviderSearchQuery,      // "A MyChart provider missing?" picker — search Epic's directory
    useAddOrgMutation,               // "A MyChart provider missing?" picker — append the chosen org
    useRemoveOrgMutation,            // delete a provider the patient added by mistake (pre-retrieval only)
    //usetTiggerHealthDataRequestMutation,
    useGetSSEEventsQuery,            // ✅ Used in: DataRequestSSEUpdates.tsx
    useGetOnboardingStatusQuery,     // ✅ Used in: OnboardingFlow.tsx, HealthWalletMain.tsx
    useLazyGetOnboardingStatusQuery, // ✅ Used in: VerificationWrapper.tsx
    useSubmitConsentMutation,        // ✅ Used in: OnboardingConsent.tsx
    useRevokeResearchConsentMutation, // ✅ Used in: HealthWalletSettings.tsx (withdraw a research consent)
    useStartVerificationMutation,    // ✅ Used in: OnboardingVerification.tsx, ReAuthFlow.tsx
    useProcessClearOAuthMutation,    // ✅ Used in: VerificationWrapper.tsx
    useSetBrowserTrustMutation,      // ✅ Used in: OnboardingFlow.tsx, OnboardingTrustBrowser.tsx, ReAuthFlow.tsx
    useDeleteHWDataMutation,          // ✅ Used in: HealthWalletSettings.tsx
    useAcknowledgeClearVerificationMutation, // ✅ Used in: OnboardingFlow.tsx
    // User-uploaded documents
    useGetUserDocumentsQuery,
    useLazyGetUserDocumentsQuery,
    useUploadUserDocumentMutation,
    useReprocessUserDocumentMutation,
    useDeleteUserDocumentMutation,
    useRenameUserDocumentMutation,
    useLazyGetUserDocumentContentQuery,
} = healthWalletApi

/*
ENDPOINT USAGE SUMMARY:

ACTIVELY USED (9 endpoints):
- useGetDashboardQuery           → HealthWalletMain.tsx, Chatbot.tsx
- useGetGroupEventsQuery         → HealthWalletMain.tsx, GroupedDetailDialog.tsx, Chatbot.tsx
- useRequestHealthDataMutation  → (not currently imported)
- useGetSSEEventsQuery           → DataRequestSSEUpdates.tsx
- useGetOnboardingStatusQuery    → OnboardingFlow.tsx, HealthWalletMain.tsx
- useSubmitConsentMutation        → OnboardingConsent.tsx
- useStartVerificationMutation    → OnboardingVerification.tsx, ReAuthFlow.tsx
- useProcessClearOAuthMutation    → VerificationWrapper.tsx
- useSetBrowserTrustMutation      → OnboardingFlow.tsx, OnboardingTrustBrowser.tsx, ReAuthFlow.tsx

AVAILABLE BUT NOT IMPORTED (4 endpoints):
⚠️ AVAILABLE BUT NOT IMPORTED (4 endpoints):
- useGetPatientInfoQuery
- useGetSummaryQuery
- useGetGroupedResourceQuery
- useRequestHealthDataMutation

❌ MISSING ENDPOINTS (referenced in old export but don't exist):
- useGetTrendsQuery
- usePatientChatMutation
- useUpdateOnboardingStateMutation
*/
